import { ReactElement, useState } from 'react';
import Layout from 'layout/outside';
import { Button, Container, Flex, Group, Modal, Paper, Space, Title } from '@mantine/core';
import Image from 'next/image';
import { IconDownload, IconHandFinger } from '@tabler/icons-react';
import Head from 'next/head';
import GrandslamCard from 'components/GrandSlam/card';
import { GetStaticProps, InferGetStaticPropsType } from 'next/types';
import { GrandSlamUser } from 'firebaseNextjs/models';
import admin from 'firebaseNextjs/serverApp';
import { useDisclosure } from '@mantine/hooks';
import GrandSlamTable from 'components/GrandSlam/table';
import useStyles from './styles';

export type TableMatchPoints = {
  pointsTotal: number;
  name: string;
  id: string;
  class: string;
  division: string;
  matchesPoints: Record<string, number>;
  matchesPd: Record<string, number>;
  category: string[];
  pd: number;
};

export const getStaticProps: GetStaticProps<{
  users: Record<string, GrandSlamUser[]>;
  globalMatchPoints: TableMatchPoints[];
}> = async () => {
  const db = admin.firestore();
  const allUsers: any = {};
  const ALL_MATCHES: string[] = ['defense', 'conrad', 'fullprotection', 'agna', 'tps'];
  type EntryGlobalTable = { points: number; pd: number };
  const tmpTableUsers: Record<string, EntryGlobalTable[]> = {};
  let globalMatchPoints: TableMatchPoints[] = [];
  const hashTableUserId: Record<
    string,
    {
      name: string;
      class: string;
      division: string;
      category: string[];
    }
  > = {};
  const hashTableUserIdMatchPoints: Record<string, Record<string, number>> = {};
  const hashTableUserIdPd: Record<string, Record<string, number>> = {};
  // eslint-disable-next-line no-restricted-syntax
  for (const match of ALL_MATCHES) {
    const ref = db.collection('GrandSlam_2024').doc(match).collection('shooters');
    // eslint-disable-next-line no-await-in-loop
    const users = await ref.get();
    if (users.size > 0) {
      allUsers[match] = users.docs.map((user) => {
        const userData = user.data();
        // if (/[a-z]/gmi.exec(userData.id) === null) {
        //   return [];
        // }
        if (!tmpTableUsers[userData.id]) {
          tmpTableUsers[userData.id] = [];
          hashTableUserId[userData.id] = {
            name: userData.name,
            class: userData.class,
            division: userData.division,
            category: userData.category || null,
          };
        }
        if (userData.pointsTotal) {
          if (!hashTableUserIdMatchPoints[userData.id]) {
            hashTableUserIdMatchPoints[userData.id] = {};
            hashTableUserIdPd[userData.id] = {};
          }
          hashTableUserIdMatchPoints[userData.id][match] = userData.pointsTotal;
          hashTableUserIdPd[userData.id][match] = +userData.pd;
          tmpTableUsers[userData.id].push({
            points: userData.pointsTotal,
            pd: +userData.pd,
          });
        }

        return userData;
      });
    }
  }
  Object.entries(tmpTableUsers).forEach(([id, obj]: [string, EntryGlobalTable[]]) => {
    if (obj.length && id) {
      const orderObj = obj.sort((a, b) => b.points - a.points).slice(0, 2);
      const pointsTotal = orderObj.reduce((c: number, s: EntryGlobalTable) => c + s.points, 0);
      const pdTotal = orderObj.reduce((c: number, s: EntryGlobalTable) => c + s.pd, 0);
      globalMatchPoints.push({
        pointsTotal,
        name: hashTableUserId[id].name,
        id,
        class: hashTableUserId[id].class,
        division: hashTableUserId[id].division,
        category: hashTableUserId[id].category,
        matchesPoints: hashTableUserIdMatchPoints[id] || [],
        matchesPd: hashTableUserIdPd[id] || [],
        pd: pdTotal,
      });
    }
  });
  globalMatchPoints = globalMatchPoints.sort((a, b) => a.pointsTotal - b.pointsTotal);
  return {
    props: { users: allUsers, globalMatchPoints },
  };
};

const Page = ({ users, globalMatchPoints }: InferGetStaticPropsType<typeof getStaticProps>) => {
  const { classes } = useStyles();
  const [modalPartialOpened, { open: openPartial, close: closePartial }] = useDisclosure(false);
  const [modalGlobalOpened, { open: openGlobal, close: closeGlobal }] = useDisclosure(false);
  const [
    modalGlobalCategoriesOpened,
    { open: openGlobalCategories, close: closeGlobalCategories },
  ] = useDisclosure(false);
  const [chartWinterData, setChartWinterData] = useState<GrandSlamUser[]>([]);
  const [chartWinterTitle, setChartWinterTitle] = useState('');
  const handleButtonWinterslam = (
    type: 'defense' | 'broni' | 'conrad' | 'fullprotection' | 'gornate' | 'agna' | 'tps'
  ) => {
    switch (type) {
      case 'defense':
        setChartWinterData(users.defense);
        setChartWinterTitle('Arcisate');
        break;
      case 'conrad':
        setChartWinterData(users.conrad);
        setChartWinterTitle('Conrad');
        break;
      case 'fullprotection':
        setChartWinterData(users.fullprotection);
        setChartWinterTitle('Full Protection');
        break;
      case 'agna':
        setChartWinterData(users.agna);
        setChartWinterTitle('Agna');
        break;
      case 'tps':
        setChartWinterData(users.tps);
        setChartWinterTitle('Tps');
        break;
    }
    openPartial();
  };

  return (
    <Container size="lg" px="xs">
      <div className={classes.boxHeader}>
        <Title
          className={classes.title}
          align="center"
          variant="gradient"
          gradient={{ from: 'red', to: 'orange', deg: 45 }}
        >
          Brownells Grand Slam 2024
        </Title>
      </div>
      <Space h="xl" />
      <Paper shadow="xs" p="md" withBorder style={{ textAlign: 'center' }}>
        <Title className={classes.subTitle} align="center">
          Locandina
        </Title>
        <Space h="md" />
        <div className={classes.boxLocandina}>
          <Image
            className={classes.imgLocandina}
            src="/grandslam/locandina.jpg"
            alt="Locandina"
            height={600}
            width={600}
          />
        </div>
      </Paper>
      <Space h="xl" />
      <Paper shadow="xs" p="md" withBorder style={{ textAlign: 'center' }}>
        <Title className={classes.subTitle} align="center">
          Regolamento
        </Title>
        <Space h="md" />
        <Button
          component="a"
          href="/grandslam/regolamento.pdf"
          variant="outline"
          leftIcon={<IconDownload size="0.9rem" />}
        >
          Scarica il regolamento 2024
        </Button>
      </Paper>
      <Space h="xl" />
      <Paper shadow="xs" p="md" withBorder>
        <Title className={classes.subTitle} align="center">
          Iscrizione
        </Title>
        <Space h="md" />
        <Group style={{ justifyContent: 'center' }} data-testid="winterslam-group-cards">
          <GrandslamCard handleButtonWinterslam={handleButtonWinterslam} type="defense" />
          <GrandslamCard handleButtonWinterslam={handleButtonWinterslam} type="fullprotection" />
          <GrandslamCard handleButtonWinterslam={handleButtonWinterslam} type="conrad" />
          <GrandslamCard handleButtonWinterslam={handleButtonWinterslam} type="agna" />
          <GrandslamCard handleButtonWinterslam={handleButtonWinterslam} type="tps" />
          <GrandslamCard handleButtonWinterslam={handleButtonWinterslam} type="vasanello" />
          <GrandslamCard handleButtonWinterslam={handleButtonWinterslam} type="gualtieri" />
          <Modal
            opened={modalPartialOpened}
            onClose={closePartial}
            fullScreen
            transitionProps={{ transition: 'fade', duration: 200 }}
          >
            <GrandSlamTable
              categories={false}
              global={false}
              elements={chartWinterData}
              title={chartWinterTitle}
            />
          </Modal>
        </Group>
      </Paper>
      <Space h="xl" />
      <Paper shadow="xs" p="md" withBorder style={{ textAlign: 'center' }}>
        <Title className={classes.subTitle} align="center">
          Classifica Finale
        </Title>
        <Space h="md" />
        <Flex direction="column" justify="center" align="center">
          <Button
            color="green"
            onClick={openGlobal}
            variant="outline"
            leftIcon={<IconHandFinger size="0.9rem" />}
          >
            Classifica Overall
          </Button>
          <Button
            color="green"
            onClick={openGlobalCategories}
            variant="outline"
            leftIcon={<IconHandFinger size="0.9rem" />}
            mt={10}
          >
            Classifica Sub Category
          </Button>
        </Flex>
        <Modal
          opened={modalGlobalOpened}
          onClose={closeGlobal}
          fullScreen
          transitionProps={{ transition: 'fade', duration: 200 }}
        >
          <GrandSlamTable
            categories={false}
            global
            elements={globalMatchPoints}
            title="Classifica Finale"
          />
        </Modal>
        <Modal
          opened={modalGlobalCategoriesOpened}
          onClose={closeGlobalCategories}
          fullScreen
          transitionProps={{ transition: 'fade', duration: 200 }}
        >
          <GrandSlamTable
            categories
            global
            elements={globalMatchPoints}
            title="Classifica Finale Sub Category"
          />
        </Modal>
      </Paper>
    </Container>
  );
};

Page.getLayout = function getLayout(page: ReactElement) {
  return (
    <>
      <Head>
        <title>Saurs - BROWNELLS IDPA Grand Slam 2024</title>
        <meta
          name="description"
          content="Segui le classifiche delle singole gare e dei punti match destinati ad eleggere i Campioni d'Italia!"
          key="desc"
        />
      </Head>
      <Layout>{page}</Layout>
    </>
  );
};

export default Page;
